import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Popover,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/Context';

import toast from 'react-hot-toast';
import { SocketNFCContext } from '../../context/SocketNFC';
import { SocketContext } from '../../context/SocketContext';
import { useTranslation } from 'react-i18next';
import {
  headerCellSx,
  dataRowSx,
  touchMenuProps,
} from '../../theme/tableTokens';
import TouchButton from '../../components/TouchButton/TouchButton';
import SearchIcon from '@mui/icons-material/Search';

import TableLoader from '../../components/TableLoader/TableLoader';
import '../../assets/css/buttons.css';


function SisteSalgs() {
  const dateInputRef = useRef(null);
  const navigate = useNavigate();

  const { hall, printer, isLogin, isBlocking, BlockingScreen } =
    useContext(AppContext);

  const { socket } = useContext(SocketContext);
  const { t } = useTranslation();

  const [rows, setRows] = useState(false);
  const [rowsFiltered, setRowsFiltered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [searchType, setSearchType] = useState('username');

  const searchTypeLabel = {
    username: t('sisteSalgs.username'),
    name: t('pep.name'),
    userID: t('sisteSalgs.userID'),
    total: t('sisteSalgs.total'),
    payCard: t('sisteSalgs.payCard'),
    payCash: t('sisteSalgs.payCash'),
    time: t('sales.time'),
  };

  const [searchValue, setSearchValue] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    const handleShoppingCarts = (data) => {
      setRows(data || []);
      setIsLoading(false);
    };

    socket.on('getShoppingCarts', handleShoppingCarts);

    return () => {
      socket.off('getShoppingCarts', handleShoppingCarts);
    };
  }, [socket]);

  useEffect(() => {
    if (!isLogin) {
      navigate('/login');
    }
  }, []);

  useEffect(() => {
    socket.emit('getShoppingCarts', { bingoName: hall, date: new Date().toISOString().slice(0, 10) });
  }, [hall]);

  useEffect(() => {
    if (!rows) return;
    let filtered = Array.isArray(rows) ? rows.filter((row) => row.bingoName === hall) : [];
    if (searchValue.trim()) {
      const val = searchValue.trim().toLowerCase();
      const guestLabel = t('kiosk.guest').toLowerCase();
      if (searchType === 'username') {
        filtered = filtered.filter((row) => {
          const effective = row.username && row.username !== '' ? row.username.toLowerCase() : guestLabel;
          return effective.includes(val);
        });
      } else if (searchType === 'name') {
        filtered = filtered.filter((row) => {
          const effective = row.name && row.name !== '' ? row.name.toLowerCase() : guestLabel;
          return effective.includes(val);
        });
      } else if (searchType === 'userID') {
        filtered = filtered.filter((row) => {
          const effective = row.userID === 0 ? '-' : String(row.userID);
          return effective.includes(val);
        });
      } else if (searchType === 'total') {
        filtered = filtered.filter((row) => String(row.total ?? '').includes(val));
      } else if (searchType === 'payCard') {
        filtered = filtered.filter((row) => String(row.payCard ?? '').includes(val));
      } else if (searchType === 'payCash') {
        filtered = filtered.filter((row) => String(row.payCash ?? '').includes(val));
      } else if (searchType === 'time') {
        filtered = filtered.filter((row) => convertToMySQLDateTime(row.date).includes(val));
      }
    }
    setRowsFiltered(filtered);
  }, [rows, searchType, searchValue]);

  function convertToMySQLDateTime(isoString) {
    const date = new Date(isoString);

    // Padding function to add leading zeros if necessary
    const pad = (number) => (number < 10 ? '0' + number : number);

    // Format the date using UTC methods
    const year = date.getUTCFullYear();
    const month = pad(date.getUTCMonth() + 1); // getUTCMonth() returns month from 0-11
    const day = pad(date.getUTCDate());
    const hours = pad(date.getUTCHours());
    const minutes = pad(date.getUTCMinutes());
    const seconds = pad(date.getUTCSeconds());

    return `${hours}:${minutes}:${seconds}`;
  }

  const [showTransactions, setShowTransactions] = useState(false);
  const [transactions, setTransactions] = useState(false);

  const checkTransactions = (row) => {
    setTransactions(row);
    setShowTransactions(true);
  };

  const backtToShops = () => {
    setTransactions(false);
    setShowTransactions(false);
  };

  const handlePrint = (shoppingCart) => {
    socket.emit('printShoppingCartsOnDemand', {
      hall: hall,
      printer: printer,
      action: 'printShoppingCartsOnDemand',
      printInfo: shoppingCart.netsTicket,
      transactions: shoppingCart.transactions,
    });
  };

  const handleSearch = () => {
    setIsLoading(true);
    socket.emit('getShoppingCarts', { bingoName: hall, date });
  };

  return (
    <>
      {isBlocking && <BlockingScreen />}
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <Grid
            container
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {/* SEARCH BAR */}
            <Grid
              item
              xs={12}
              sx={{
                height: '190px',
                maxHeight: '190px',
                display: 'flex',
                alignItems: 'center',
                px: 3,
                gap: 2,
                borderBottom: '1px solid #2a3a6e',
              }}
            >
              {!showTransactions ? (
                <>
                  <FormControl
                    sx={{
                      minWidth: 180,
                      backgroundColor: '#232a45',
                      borderRadius: '4px',
                    }}
                  >
                    <InputLabel
                      sx={{ color: 'white !important', fontSize: '20px' }}
                    >
                      {t('ticketHistoric.searchType')}
                    </InputLabel>
                    <MuiSelect
                      MenuProps={touchMenuProps}
                      value={searchType}
                      label={t('ticketHistoric.searchType')}
                      onChange={(e) => setSearchType(e.target.value)}
                      sx={{
                        color: 'white',
                        height: '90px',
                        fontSize: '24px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#365ab8',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#fcad89',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#fcad89',
                        },
                        '& .MuiSvgIcon-root': { color: 'white' },
                      }}
                    >
                      <MenuItem value="username">{t('sisteSalgs.username')}</MenuItem>
                      <MenuItem value="name">{t('pep.name')}</MenuItem>
                      <MenuItem value="userID">{t('sisteSalgs.userID')}</MenuItem>
                      <MenuItem value="total">{t('sisteSalgs.total')}</MenuItem>
                      <MenuItem value="payCard">{t('sisteSalgs.payCard')}</MenuItem>
                      <MenuItem value="payCash">{t('sisteSalgs.payCash')}</MenuItem>
                      <MenuItem value="time">{t('sales.time')}</MenuItem>
                    </MuiSelect>
                  </FormControl>

                  <TextField
                    sx={{
                      flex: 1,
                      backgroundColor: '#232a45',
                      '& .MuiOutlinedInput-root': {
                        height: '90px',
                        '& fieldset': { borderColor: '#365ab8' },
                        '&:hover fieldset': { borderColor: '#fcad89' },
                        '&.Mui-focused fieldset': { borderColor: '#fcad89' },
                      },
                      '& input': { color: 'white', fontSize: '24px' },
                      '& label': {
                        color: 'white !important',
                        fontSize: '20px',
                      },
                      '& label.Mui-focused': { color: 'white !important' },
                      '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                        transform: 'translate(14px, 30px) scale(1)',
                      },
                    }}
                    label={searchTypeLabel[searchType] || ''}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    autoFocus
                autoComplete="off"
                    variant="outlined"
                  />

                  <TextField
                    type="date"
                    label={t('sales.searchByDate')}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    inputRef={dateInputRef}
                    onClick={() => dateInputRef.current?.showPicker?.()}
                    sx={{
                      width: '310px',
                      backgroundColor: '#232a45',
                      cursor: 'pointer',
                      '& .MuiOutlinedInput-root': {
                        height: '90px',
                        cursor: 'pointer',
                        '& fieldset': { borderColor: '#365ab8' },
                        '&:hover fieldset': { borderColor: '#fcad89' },
                        '&.Mui-focused fieldset': { borderColor: '#fcad89' },
                      },
                      '& input': {
                        color: 'white',
                        fontSize: '24px',
                        colorScheme: 'dark',
                        cursor: 'pointer',
                      },
                      '& label': {
                        color: 'white !important',
                        fontSize: '20px',
                      },
                      '& label.Mui-focused': { color: 'white !important' },
                    }}
                  />

                  <TouchButton
                    tone="confirm"
                    onClick={handleSearch}
                    startIcon={<SearchIcon />}
                    sx={{ height: '90px', minWidth: '160px', flexShrink: 0 }}
                  >
                    {t('menu.search')}
                  </TouchButton>
                </>
              ) : (
                <TouchButton
                  tone="confirm"
                  onClick={backtToShops}
                  startIcon={<SearchIcon />}
                  sx={{ height: '90px', minWidth: '160px', flexShrink: 0 }}
                >
                  {t('sisteSalgs.backToSells')}
                </TouchButton>
              )}
            </Grid>

            {/*TABLE*/}
            {!showTransactions ? (
              <Grid
                item
                xs={12}
                sx={{
                  width: '100%',
                  padding: '10px',
                  height: '100%',
                }}
              >
                {isLoading ? (
                  <TableLoader />
                ) : (
                  <TableContainer
                    component={Paper}
                    className="sp-scroll"
                    sx={{
                      maxHeight: 'calc(100% - 190px)',
                      marginBottom: '190px',
                      overflowY: 'auto',
                      backgroundColor: '#1a2240',
                    }}
                  >
                    <Table
                      sx={{
                        color: 'white',
                        background: '#1a2240',
                      }}
                      stickyHeader
                      aria-label="simple table"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell align="center" sx={headerCellSx}>
                            {t('sales.time')}
                          </TableCell>
                          {/*
                        <TableCell align="center" sx={headerCellSx}>
                          {t('sisteSalgs.operator')}
                        </TableCell>
                        */}
                          <TableCell align="center" sx={headerCellSx}>
                            {t('sisteSalgs.userID')}
                          </TableCell>

                          <TableCell align="center" sx={headerCellSx}>
                            {t('sisteSalgs.username')}
                          </TableCell>
                          <TableCell align="center" sx={headerCellSx}>
                            {t('pep.name')}
                          </TableCell>

                          <TableCell align="center" sx={headerCellSx}>
                            {t('sisteSalgs.total')}
                          </TableCell>
                          <TableCell align="center" sx={headerCellSx}>
                            {t('sisteSalgs.payCard')}
                          </TableCell>
                          <TableCell align="center" sx={headerCellSx}>
                            {t('sisteSalgs.payCash')}
                          </TableCell>
                          <TableCell align="center" sx={headerCellSx}>
                            {t('sisteSalgs.payAccount')}
                          </TableCell>
                          <TableCell align="center" sx={headerCellSx}>
                            {t('sisteSalgs.change')}
                          </TableCell>
                          <TableCell align="center" sx={headerCellSx}>
                            {t('sisteSalgs.transactions')}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={headerCellSx}
                          ></TableCell>
                        </TableRow>
                      </TableHead>
                      {rowsFiltered && (
                        <TableBody>
                          {rowsFiltered.map((row, index) =>
                            row.bingoName !== hall ? null : (
                              <TableRow
                                key={index}
                                sx={dataRowSx}
                              >
                                <TableCell
                                  component="th"
                                  scope="row"
                                  align="center"
                                >
                                  {convertToMySQLDateTime(row.date)}
                                </TableCell>
                                {/*
                              <TableCell align="center">
                                {row.operator}
                              </TableCell>
                              */}
                                <TableCell align="center">
                                  {row.userID === 0 ? '—' : row.userID}
                                </TableCell>
                                {/*
                              <TableCell align="center">
                                {row.userInfo.firstname
                                  ? row.userInfo.firstname
                                  : "-"}
                              </TableCell>
                              */}
                                <TableCell align="center">
                                  {row.username && row.username !== '' ? row.username : t('kiosk.guest')}
                                </TableCell>
                                <TableCell align="center">
                                  {row.name && row.name !== '' ? row.name : t('kiosk.guest')}
                                </TableCell>
                                <TableCell align="center">
                                  <Box
                                    sx={{ color: '#8fa3d4', fontWeight: 600 }}
                                  >
                                    {row.total}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Box
                                    sx={{ color: '#8fa3d4', fontWeight: 600 }}
                                  >
                                    {row.payCard}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Box
                                    sx={{ color: '#8fa3d4', fontWeight: 600 }}
                                  >
                                    {row.payCash}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Box
                                    sx={{ color: '#8fa3d4', fontWeight: 600 }}
                                  >
                                    {row.paySpillerkonto}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Box
                                    sx={{ color: '#8fa3d4', fontWeight: 600 }}
                                  >
                                    {row.exchange}
                                  </Box>
                                </TableCell>

                                {/*
          <TableCell align="center">
            <FormControlLabel
              control={
                <CheckBox
                  checked={row.printed}
                  onChange={() => {}}
                  name="checked"
                  color="primary"
                />
              }
              label={""}
            />
          </TableCell>
          <TableCell align="center">
            <TouchButton
              tone="info"
              sx={{ minWidth: 90, height: 52, fontSize: 17, px: 1.5 }}
              onClick={() => {
                // handle click
              }}
            >
              {t("sisteSalgs.printAgain")}
            </TouchButton>
          </TableCell>
          */}

                                <TableCell align="center">
                                  <TouchButton
                                    tone="info"
                                    sx={{ minWidth: 90, height: 52, fontSize: 17, px: 1.5 }}
                                    onClick={() => {
                                      checkTransactions(row);
                                    }}
                                  >
                                    {t('sisteSalgs.transactions')}
                                  </TouchButton>
                                </TableCell>
                                <TableCell align="center">
                                  <TouchButton
                                    tone="info"
                                    sx={{ minWidth: 90, height: 52, fontSize: 17, px: 1.5 }}
                                    onClick={() => {
                                      handlePrint(row);
                                    }}
                                  >
                                    {t('sisteSalgs.printAgain')}
                                  </TouchButton>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            ) : (
              <Grid
                item
                xs={12}
                sx={{
                  width: '100%',
                  padding: '10px',
                  height: '100%',
                }}
              >
                {isLoading ? (
                  <TableLoader />
                ) : (
                  <TableContainer
                    component={Paper}
                    className="sp-scroll"
                    sx={{
                      maxHeight: 'calc(100% - 190px)',
                      marginBottom: '190px',
                      backgroundColor: '#1a2240',
                    }}
                  >
                    <Table
                      sx={{
                        color: 'white',
                        background: '#1a2240',
                      }}
                      stickyHeader
                      aria-label="simple table"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell align="center" sx={headerCellSx}>
                            {t('sales.time')}
                          </TableCell>
                          <TableCell align="center" sx={headerCellSx}>
                            {t('sisteSalgs.operator')}
                          </TableCell>
                          <TableCell align="center" sx={headerCellSx}>
                            {t('sisteSalgs.userID')}
                          </TableCell>
                          {/*
                        <TableCell align="center" sx={headerCellSx}>
                          {t('sisteSalgs.username')}
                        </TableCell>
                        */}
                          <TableCell align="center" sx={headerCellSx}>
                            {t('sisteSalgs.amount')}
                          </TableCell>
                          <TableCell align="center" sx={headerCellSx}>
                            {t('sisteSalgs.description')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      {transactions.transactions && (
                        <TableBody>
                          {transactions.transactions.map(
                            (transaction, index) => (
                              <TableRow
                                key={index}
                                sx={dataRowSx}
                              >
                                <TableCell
                                  component="th"
                                  scope="row"
                                  align="center"
                                >
                                  {convertToMySQLDateTime(transactions.date)}
                                </TableCell>

                                <TableCell align="center">
                                  {transactions.operator}
                                </TableCell>
                                <TableCell align="center">
                                  {transactions.userID}
                                </TableCell>
                                {/*
                            <TableCell align="center">
                              {transactions.userInfo.firstname
                                ? transactions.userInfo.firstname
                                : '-'}
                            </TableCell>
                            */}
                                <TableCell align="center">
                                  <Box
                                    sx={{ color: '#8fa3d4', fontWeight: 600 }}
                                  >
                                    {transaction.amount}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  {transaction.name}
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default SisteSalgs;
